// src/components/Digital/Digital.js
import React, { useEffect, useMemo, useRef } from "react";
import "./Digital.css";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Assets — Video + service icons
import digitalVideo from "../../assets/digitalvid.mp4";
import digital1 from "../../assets/digital1.webp";
import digital2 from "../../assets/digital2.webp";
import digital3 from "../../assets/digital3.webp";
import digital4 from "../../assets/digital4.webp";
import digital5 from "../../assets/digital5.webp";
import digital6 from "../../assets/digital6.webp";

// Gallery (6 images -> 2x3 grid on mobile)
import dw1 from "../../assets/digitalwork1.webp";
import dw2 from "../../assets/digitalwork2.webp";
import dw3 from "../../assets/digitalwork3.webp";
import dw4 from "../../assets/digitalwork4.webp";
import dw5 from "../../assets/digitalwork5.webp";
import dw6 from "../../assets/digitalwork6.webp";

gsap.registerPlugin(ScrollTrigger);

// =========================
// Data — Capabilities
// =========================
const capabilities = [
  {
    id: "digital-marketing",
    title: "Digital Marketing",
    points: [
      "Social Media Marketing: Promote across Facebook, Instagram, LinkedIn, Twitter, TikTok, Snapchat, and YouTube—driving awareness, traffic, and leads via organic and paid content.",
      "SEO & SEM: Optimize for organic ranking while using paid search (e.g., Google Ads) to capture high-intent traffic quickly.",
      "PPC & Google Ads: Pay-per-click campaigns targeted by keyword, location, and interests to drive immediate conversions.",
      "Email Marketing & Automation: Segment, schedule, and personalize at scale with automated flows that nurture and convert.",
      "Influencer & Affiliate Marketing: Extend reach and trust with creator partnerships and performance-based affiliates.",
      "Web Analytics & Performance Tracking: Measure behavior, campaigns, and ROI to iterate and improve.",
    ],
  },
  {
    id: "content-creation",
    title: "Content Creation",
    points: [
      "Copywriting & Blogging: SEO-friendly, persuasive writing for sites, blogs, and promos that drives action.",
      "Video Production & Editing: Script-to-screen video for promos, explainers, and testimonials.",
      "Social Content (Graphics, Reels, Stories, Carousels, UGC): Platform-native formats built for engagement and trust.",
      "Podcast: Interviews, discussions, and storytelling to build authority and deepen relationships.",
      "Motion Graphics & Animations: Explain, promote, or entertain with clear, engaging visuals.",
    ],
  },
  {
    id: "branding",
    title: "Branding & Rebranding",
    points: [
      "Logo & Identity Design: Memorable, meaningful symbols and marks.",
      "Brand Guidelines & Strategy: Voice, visuals, and usage rules for consistency.",
      "Visual Identity System: Typography, palette, iconography, and graphics that scale.",
      "Brand Storytelling & Positioning: Mission, values, and differentiated messaging.",
      "Product & Packaging Design: Beautiful, functional packaging that stands out.",
      "Reputation & Perception: Reviews, PR, and engagement to shape public view.",
    ],
  },
  {
    id: "strategic-planning",
    title: "Strategic Planning",
    points: [
      "Marketing Strategy Development: Align channels, messaging, and timing with business goals.",
      "Campaign Planning & Execution: Briefs, calendars, and delivery with post-mortems.",
      "Budget Allocation & ROI: Invest where it performs—and prove it.",
      "Go-To-Market (GTM): Positioning, pricing, distribution, and promotion for launches.",
      "Digital Transformation: Tools and workflows that scale performance and CX.",
    ],
  },
  {
    id: "web-mobile-dev",
    title: "Web & Mobile Development",
    points: [
      "Responsive Website Design: Seamless across desktop, tablet, and mobile.",
      "Custom Web Development: Modern stacks for bookings, dashboards, and more.",
      "E-commerce Platforms: Catalogs, carts, payments—secure and conversion-focused.",
      "Mobile App Development: Native and cross-platform apps with smooth UX.",
      "UX/UI Design: Beautiful, usable interfaces that delight and convert.",
    ],
  },
  {
    id: "media-planning",
    title: "Media Planning & Buying",
    points: [
      "Media Buying Strategy & Budget: Right channels, right volumes, right price.",
      "Print & Digital Placements: Newspapers, magazines, websites, and apps.",
      "Programmatic & Native Ads: Real-time buying and content-blended formats.",
      "Performance Tracking & Optimization: Monitor, test, and scale winners.",
    ],
  },
];

// =========================
// Subcomponent — Capabilities (rows)
// =========================
const CapabilitiesSection = () => {
  return (
    <section id="capabilities" className="capabilities capabilities--rows">
      <h2 className="capabilities-title">
        <span className="hollow">OUR</span> <span className="highlight">CAPABILITIES</span>
      </h2>

      <div className="cap-rows">
        {capabilities.map((block) => (
          <div key={block.id} id={block.id} className="cap-row gsap-cap-row">
            <div className="cap-title-wrap">
              <h3 className="cap-title">
                {block.title}
                {block.subtitle && <span className="cap-subtitle"> · {block.subtitle}</span>}
              </h3>
            </div>
            <div className="cap-body">
              {block.points.map((p, i) => (
                <p key={i} className="cap-text">{p}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// =========================
const Digital = () => {
  const rootRef = useRef(null);
  const galleryImages = useMemo(() => [dw1, dw2, dw3, dw4, dw5, dw6], []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Intro text (scrubbed)
      gsap.fromTo(
        ".gsap-text",
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".gsap-text",
            start: "top 85%",
            end: "top 55%",
            scrub: 1,
          },
        }
      );

      // Service boxes (alternate left/right, scrubbed)
      gsap.utils.toArray(".gsap-service").forEach((box, i) => {
        gsap.fromTo(
          box,
          { x: i % 2 === 0 ? -80 : 80, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: box,
              start: "top 85%",
              end: "top 55%",
              scrub: 1,
            },
          }
        );
      });

      // Gallery images (scrubbed rise/scale)
      gsap.fromTo(
        ".gsap-gallery img",
        { y: 20, scale: 0.97, opacity: 0 },
        {
          y: 0,
          scale: 1,
          opacity: 1,
          ease: "power2.out",
          stagger: 0.06,
          scrollTrigger: {
            trigger: ".gsap-gallery",
            start: "top 85%",
            end: "top 40%",
            scrub: 1,
          },
        }
      );

      // Capabilities rows (scrubbed)
      gsap.fromTo(
        ".gsap-cap-row",
        { y: 26, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: "power2.out",
          stagger: 0.08,
          scrollTrigger: {
            trigger: ".capabilities--rows",
            start: "top 90%",
            end: "top 40%",
            scrub: 1,
          },
        }
      );

      // Subtle video zoom on scroll (scrub:1)
      gsap.to(".digital-video", {
        scale: 1.06,
        ease: "none",
        scrollTrigger: {
          trigger: ".digital-video-container",
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="digital-wrapper" ref={rootRef}>
      {/* 🔁 Background Video */}
      <div className="digital-video-container" aria-hidden>
        <video className="digital-video" autoPlay muted loop playsInline preload="metadata">
          <source src={digitalVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* 📄 360 Campaign Text */}
      <section className="digital-text-section gsap-text">
        <h2 className="digital-heading">360° Digital Campaigns</h2>
        <p className="digital-paragraph">
          Our 360° Digital Campaigns provide an all-encompassing approach that seamlessly integrates various
          digital marketing channels to create a unified and powerful brand presence. We combine the power of
          social media marketing, search engine optimization (SEO), search engine marketing (SEM), pay-per-click
          advertising (PPC), email marketing and automation, influencer partnerships, and affiliate marketing into
          a single, coherent strategy designed to engage audiences at every touchpoint. Each campaign is built on
          in-depth market research and data analysis to precisely target the right audiences and deliver
          personalized messaging that resonates. By using advanced analytics and continuous performance
          monitoring, we adjust and refine campaigns in real-time to ensure maximum effectiveness and return on
          investment. The result is a highly coordinated, multi-channel effort that not only boosts brand awareness
          but also drives customer loyalty, enhances conversion rates, and ultimately fuels long-term business
          growth.
        </p>
      </section>

      {/* 🪟 Services */}
      <section className="digital-services-section">
        <h2 className="digital-services-title">
          <span className="hollow">OUR</span> <span className="highlight">SERVICES</span>
        </h2>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital1} alt="Social media marketing icon" loading="lazy" decoding="async" />
            <p>
              Social Media
              <br />
              <span>Marketing</span>
            </p>
          </div>
          <div className="service-right">
            Promote on Facebook, Instagram, LinkedIn, Twitter, TikTok, Snapchat, and YouTube—engage users, build
            awareness, drive traffic, and generate leads with organic and paid content.
          </div>
        </div>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital2} alt="SEO & SEM icon" loading="lazy" decoding="async" />
            <p>
              Search Engine Optimization (SEO) & Search
              <br />
              <span>Engine Marketing (SEM)</span>
            </p>
          </div>
          <div className="service-right">
            SEO improves organic rankings; SEM uses paid ads like Google Ads to appear prominently, capturing targeted
            traffic quickly.
          </div>
        </div>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital3} alt="PPC & Google Ads icon" loading="lazy" decoding="async" />
            <p>
              Pay-Per-Click Advertising (PPC) &
              <br />
              <span>Google Ads</span>
            </p>
          </div>
          <div className="service-right">
            Run precise, high-intent PPC campaigns by keyword, location, and interests to drive immediate conversions.
          </div>
        </div>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital4} alt="Email marketing icon" loading="lazy" decoding="async" />
            <p>
              Email Marketing &
              <br />
              <span>Automation</span>
            </p>
          </div>
          <div className="service-right">
            Segment audiences, schedule sequences, and deliver personalized emails at the right time to nurture and
            convert.
          </div>
        </div>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital5} alt="Influencer & affiliate icon" loading="lazy" decoding="async" />
            <p>
              Influencer Marketing & Affiliate
              <br />
              <span>Marketing</span>
            </p>
          </div>
          <div className="service-right">
            Leverage creators’ reach and performance-based affiliates to extend audience and build trust.
          </div>
        </div>

        <div className="digital-service-box gsap-service">
          <div className="service-left">
            <img src={digital6} alt="Analytics icon" loading="lazy" decoding="async" />
            <p>
              Web Analytics & Performance
              <br />
              <span>Tracking</span>
            </p>
          </div>
          <div className="service-right">
            Track behavior and ROI; iterate with data to improve campaign performance.
          </div>
        </div>
      </section>

      {/* 🎯 Our Work (2x3 on mobile) */}
      <section className="digital-gallery-section">
        <h2 className="gallery-heading">
          <span className="empty-text">OUR</span> <span className="filled-text">WORK</span>
        </h2>

        <div className="gallery-grid gsap-gallery">
          {galleryImages.map((src, idx) => (
            <img key={idx} src={src} alt={`Portfolio ${idx + 1}`} className="gallery-image" loading="lazy" decoding="async" />
          ))}
        </div>
      </section>

      {/* 🔧 Capabilities (rows) */}
      <CapabilitiesSection />
    </div>
  );
};

export default Digital;
